import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';

import css from './index.scss';

import messages from './messages';

import {
    YEAR_GROUPS,
    STATUSES,
    STATUSES_AS_KEY,
    COURSE_CREATOR_ROUTE,
    DEFAULT_COURSES_ROUTE,
    ARCHIVE_STATUS_KEY,
    RETIRED_COURSE_ID,
    DEFAULT_CURRICULUM_TYPE_ID,
    COURSE_CREATOR_SUB_ROUTES
} from '../../constants';

import { getMasterCoursesCountries } from '../master-courses/actions';
import { getDefaultCourses, applySearchFilter, archiveSelectedCourse } from './actions';

import { getProductList } from '../../helpers/utils';

import CourseFilter from '../../components/course-filter';
import Button from '../../components/button';
import DropdownList from '../../components/dropdown-list';
import TextInput from '../../components/text-input';
import withLoader from '../../components/with-loader';
import Header from '../../components/header';
import CourseTypeSelector from '../../components/course-type-selector/course-type-selector';
import Container from '../../components/container';
import {
    DataTable,
    CellRenderer,
    ActionRenderer,
    DateTimeRenderer
} from '../../components/data-table';
import ArchiveCourse from './components/archive-course/archive-course';

export class ThreePCourses extends PureComponent {
    static propTypes = {
        intl: intlShape.isRequired,
        getMasterCoursesCountries: PropTypes.func,
        countries: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ]),
        isCountryLoaded: PropTypes.bool,
        getDefaultCourses: PropTypes.func,
        courses: PropTypes.arrayOf(PropTypes.object),
        applySearchFilter: PropTypes.func,
        country: PropTypes.string,
        year: PropTypes.string,
        status: PropTypes.string,
        selectedProductId: PropTypes.number,
        archiveSelectedCourse: PropTypes.func
    }

    constructor(props) {
        super(props);
        this.editCourse = this.editCourse.bind(this);
        this.onSubjectChange = this.onSubjectChange.bind(this);
        this.handleOnCourseTypeChange = this.handleOnCourseTypeChange.bind(this);
        this.textInputRef = React.createRef();
        this.state = {
            selectedCourseType: DEFAULT_CURRICULUM_TYPE_ID
        };

        this.courseTypeOptions = [
            { value: 'Curriculum type', key: -1 },
            { value: 'Global', key: 0 },
            { value: 'Country Specific', key: 2 }
        ];

        this.globalCourseId = 0;
    }

    static contextTypes = {
        router: PropTypes.object.isRequired
    };

    componentDidMount() {
        const { isCountryLoaded } = this.props;
        if (!isCountryLoaded) {
            this.props.getMasterCoursesCountries();
        }
        this.props.getDefaultCourses(this.props.selectedProductId);
    }

    editCourse = (selectedItem, actionType) => {
        const { id, version, productId } = selectedItem;
        this.context.router.history.push(`${COURSE_CREATOR_ROUTE}${DEFAULT_COURSES_ROUTE}/${actionType}/${productId}/${id}/version/${version}`);
    };

    archiveCourse = (selectedCourse) => {
        const { productId, courseId, courseVersion } = selectedCourse;
        this.props.archiveSelectedCourse({ productId, courseId, courseVersion });
        this.textInputRef.current.handleClearText();
    };

    onSubjectChange(name, value) {
        this.props.getDefaultCourses(parseInt(value, 10));
    }

    handleOnCourseTypeChange = (value) => {
        this.props.applySearchFilter('country', '');
        this.setState({
            selectedCourseType: Number(value)
        });
    };

    render() {
        const emptyContent = <div><b>{this.props.intl.formatMessage(messages.emptyMessage)}</b></div>;
        const headers = [
            {
                id: 'courseName',
                accessor: course => course.courseName,
                Header: 'Course Name',
                sort: { sortAs: 'string', sortDir: 'desc' },
                Cell: CellRenderer
            },
            {
                id: 'version',
                accessor: course => `${course.id}v${course.version}`,
                Header: 'Version',
                width: 80,
                Cell: CellRenderer,
                sortable: false
            },
            {
                id: 'Subject',
                accessor: () => getProductList(this.props.selectedProductId),
                Header: 'Product',
                width: 150,
                Cell: CellRenderer,
                sortable: false
            },
            {
                id: 'country',
                accessor: course => course.country,
                Header: 'Country',
                width: 150,
                Cell: CellRenderer
            },
            { accessor: 'state', Header: 'State', Cell: CellRenderer, width: 100 },
            {
                id: 'dateModified',
                accessor: course => course.dateModified,
                Header: 'Date Modified',
                sort: { sortAs: 'string', sortDir: 'desc' },
                Cell: DateTimeRenderer,
                width: 200
            },
            {
                id: 'statusLabel',
                accessor: course => course.statusLabel,
                Header: 'Status',
                Cell: CellRenderer,
                width: 120
            },
            {
                id: 'hasChanges',
                accessor: course => course.hasChanges,
                Header: 'Changes',
                width: 100,
                Cell: CellRenderer,
                sortable: false
            },
            {
                id: 'action',
                accessor: course => course.action,
                Header: 'Action',
                sortable: false,
                width: 250
            }
        ];

        const courseActions = (item) => {
            const {
                COPY,
                EDIT
            } = COURSE_CREATOR_SUB_ROUTES;

            if (Number(this.props.status) !== ARCHIVE_STATUS_KEY) {
                return (<div className={css.actionButtons}>
                    <div>
                        {
                            Number(item.status) === RETIRED_COURSE_ID ?
                                <span /> :
                                <Button basic handleOnClick={() => this.editCourse(item, COPY)} label="Copy" />
                        }
                    </div>
                    <div>
                        <Button
                            basic
                            handleOnClick={() => this.editCourse(item, EDIT)}
                            label={Number(item.status) === RETIRED_COURSE_ID ? 'View' : 'Edit'} />
                    </div>
                    <div>
                        {
                            Number(item.status) === RETIRED_COURSE_ID ?
                                <span /> :
                                <ArchiveCourse
                                    archiveSelectedCourse={this.archiveCourse}
                                    selectedCourse={item}
                                    textInputRef={this.textInputRef}
                                />
                        }
                    </div>
                </div>);
            } return null;
        };

        let filteredCourses = this.props.courses.filter((item) => {
            if (item.status &&
                ((Number(this.props.status) === (ARCHIVE_STATUS_KEY) ||
                Number(this.props.status) === (RETIRED_COURSE_ID)) ? true :
                    ((Number(item.status) !== ARCHIVE_STATUS_KEY) &&
                    (Number(item.status) !== RETIRED_COURSE_ID)))
            ) {
                item.hasChanges = item.changes ? 'YES' : 'NO';
                item.statusLabel = STATUSES_AS_KEY[item.status];
                item.action = <ActionRenderer>{courseActions(item)}</ActionRenderer>;
                return item;
            } return null;
        });

        if (this.state.selectedCourseType !== DEFAULT_CURRICULUM_TYPE_ID) {
            filteredCourses = filteredCourses.filter(course => course.accessLevelType === this.state.selectedCourseType);
        }

        return (
            <div>
                <Header subtitle="3P Courses" title="Internal Course Manager" />
                <Container>
                    <CourseFilter>
                        <TextInput
                            clearText
                            handleOnChange={value => this.props.applySearchFilter('search', value)}
                            ref={this.textInputRef} />
                        <DropdownList
                            onChange={this.onSubjectChange}
                            optionKey="id"
                            options={getProductList()}
                            optionText="name"
                            value={this.props.selectedProductId} />
                        <CourseTypeSelector
                            onChange={this.handleOnCourseTypeChange}
                            options={this.courseTypeOptions}
                        />
                        <DropdownList
                            disabled={Number(this.state.selectedCourseType) === this.globalCourseId}
                            name="country"
                            onChange={this.props.applySearchFilter}
                            optionKey="name"
                            options={this.props.countries}
                            optionText="name"
                            placeholder="All countries"
                            value={this.props.country} />
                        <DropdownList
                            name="year"
                            onChange={this.props.applySearchFilter}
                            optionKey="id"
                            options={YEAR_GROUPS}
                            placeholder="All years"
                            value={this.props.year} />
                        <DropdownList
                            name="status"
                            onChange={this.props.applySearchFilter}
                            optionKey="id"
                            options={STATUSES}
                            placeholder="Status"
                            value={this.props.status} />
                    </CourseFilter>
                    <DataTable
                        className={'-striped'}
                        columns={headers}
                        data={filteredCourses}
                        defaultPageSize={50}
                        noDataText={emptyContent} />
                </Container>
            </div>
        );
    }
}

function mapStateToProps(state = {}) {
    return {
        countries: state.masterCourse.countries.items || [],
        isCountryLoaded: state.masterCourse.countries.isLoaded,
        courses: state.defaultCourse.filteredCourses,
        isLoading: state.defaultCourse.isLoading,
        country: state.defaultCourse.filters.country,
        year: state.defaultCourse.filters.year,
        status: state.defaultCourse.filters.status,
        selectedProductId: state.defaultCourse.selectedProductId
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getMasterCoursesCountries,
        getDefaultCourses,
        applySearchFilter,
        archiveSelectedCourse
    }, dispatch);
}

const ThreePCoursesWithLoader = withLoader('isLoading')(ThreePCourses);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ThreePCoursesWithLoader));



// WEBPACK FOOTER //
// ./app/containers/default-courses/index.js